import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import SickOutlinedIcon from '@mui/icons-material/SickOutlined';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import { Stack, Typography } from '@mui/material';
import { BarChart, Gauge, LineChart } from '@mui/x-charts';
import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import { useAppSelector } from '../../store/hooks';
import { ReactComponent as HomeSvg } from '../../svg/home.svg';
import AbsenceUtils from '../../utils/AbsenceUtils';
import DayjsUtils from '../../utils/DayjsUtils';
import TimeUtils from '../../utils/TimeUtils';
import HomeCard from './HomeCard';

const UserHomePage = () => {
  const { user, profile } = useAppSelector((state) => state.auth);
  const { sumMonth, firstTimeDay } = useAppSelector((state) => state.times);
  const { absences, sickDays } = useAppSelector((state) => state.absences);

  const [noHolidays, setNoHolidays] = useState<number>(0);
  const [noSickDays, setNoSickDays] = useState<number>(0);
  const [noAbsencesInMonth, setNoAbsencesInMonth] = useState<number>(0);
  const [balanceWorkMinutes, setBalanceWorkMinutes] = useState<number>(0);
  const [balanceAvailableMinutes, setBalanceAvailableMinutes] = useState<number>(0);
  const [workDaysThisMonth, setWorkDaysThisMonth] = useState<number>(0);

  const [minGauge, setMinGauge] = useState<number>(0);
  const [maxGauge, setMaxGauge] = useState<number>(0);

  const [myStartfrom, setMyStartfrom] = useState<Dayjs | null>(null);
  const to = DayjsUtils.endOfCurrentYear();
  const firstDayOfMonth = DayjsUtils.firstDayOfCurrentMonth();
  const startOfCurrentYear = DayjsUtils.startOfCurrentYear();

  const evaluateTimes = useAppSelector((state) => state.times.evaluateTimes);

  const dataset = evaluateTimes.map((item) => ({
    month: DayjsUtils.monthStringByNumber(item.month),
    workingTime: (item.workingTime?.balance || 0) / 60,
    availableTime: (item.availableTime?.balance || 0) / 60,
  }));

  /*
    Set the first day of the year as the start date
  */
  useEffect(() => {
    if (firstTimeDay) {
      if (firstTimeDay.isAfter(startOfCurrentYear)) {
        setMyStartfrom(firstTimeDay);
      } else {
        setMyStartfrom(startOfCurrentYear);
      }
    }
  }, [firstTimeDay]);
  /*
    Calculate all the data for the user's dashboard.
  */
  useEffect(() => {
    if (profile && myStartfrom) {
      // calculate left Holidays for this year
      const resHolidays = AbsenceUtils.getHolidaysByTimestampRange(absences, myStartfrom.unix(), to.unix());

      if (resHolidays) {
        setNoHolidays(profile.holidays - resHolidays.length);
      } else {
        setNoHolidays(profile.holidays);
      }

      // absences in this month
      const resAbsencesThisMonth = AbsenceUtils.getAbsencesByTimestampRange(absences, firstDayOfMonth.unix(), DayjsUtils.todayEnd().unix());
      if (resAbsencesThisMonth) setNoAbsencesInMonth(resAbsencesThisMonth.length);

      // calculate sickness Days for this year
      const resSickDays = AbsenceUtils.getSickDaysByTimestampRange(sickDays, myStartfrom.unix(), to.unix());
      setNoSickDays(resSickDays.length);

      // Calculate personal work days this month
      const resWorkDaysMonth = TimeUtils.workDaysTillTodayInMonthByStartDate(firstDayOfMonth, profile);
      setWorkDaysThisMonth(resWorkDaysMonth);

      // set min and manx gauge values
      setMinGauge(profile.holidays - noHolidays);
      setMaxGauge(profile.holidays);
    }
  }, [profile, myStartfrom]);

  /*
    Calculate Saldo Work Time and Available Time
  */
  useEffect(() => {
    if (sumMonth && profile) {
      const targetDays = workDaysThisMonth - noAbsencesInMonth;

      const targetWorkingMinutes = targetDays * profile.workingtime;
      const targetAvailableMinutes = targetDays * profile.availabletime;

      setBalanceWorkMinutes(sumMonth.workingTime - targetWorkingMinutes);
      setBalanceAvailableMinutes(sumMonth.availableTime - targetAvailableMinutes);
    }
  }, [sumMonth, workDaysThisMonth]);

  return (
    <Stack direction="column" alignItems="center" spacing={0}>
      <HomeSvg width="150px" height="150px" />

      <Typography
        variant="h4"
        gutterBottom
        sx={{
          background: 'linear-gradient(to right, #49a7cc, #ffa680)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
        Hallo {user?.displayName}
      </Typography>
      <Typography variant="body1" align="center" gutterBottom>
        👋 Willkommen zuück! Wir haben dich 😻 vermisst.
      </Typography>
      <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" gap={2} pt={2} width="100%">
        {profile && (
          <HomeCard title="Freie Urlaubstage" SvgIcon={AirplanemodeActiveIcon} subtitle=" In diesem Jahr">
            <Stack direction="column" justifyContent="center" alignItems="center" width="100%">
              <Gauge
                value={minGauge}
                startAngle={-110}
                endAngle={110}
                width={250}
                height={150}
                text={() => `${minGauge} / ${maxGauge}`}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              />
            </Stack>
          </HomeCard>
        )}

        <HomeCard title="Arbeitszeit am Kind Saldo" SvgIcon={AccessTimeOutlinedIcon} subtitle="Im aktuellen Monat">
          <Typography variant="h4" color="text.primary">
            {`${TimeUtils.negativeMinutesToTime(balanceWorkMinutes)} h`}
          </Typography>
        </HomeCard>

        <HomeCard title="Verfügungszeit Saldo" SvgIcon={PsychologyAltOutlinedIcon} subtitle="Im aktuellen Monat">
          <Typography variant="h4" color="text.primary">
            {`${TimeUtils.negativeMinutesToTime(balanceAvailableMinutes)} h`}
          </Typography>
        </HomeCard>

        <HomeCard title="Krankheitstage" SvgIcon={SickOutlinedIcon} subtitle="In diesem Jahr">
          <Typography variant="h4" color="text.primary">
            {`${noSickDays} Tg.`}
          </Typography>
        </HomeCard>

        {evaluateTimes && (
          <HomeCard title="Verfügungszeit-Saldo" subtitle="je Monat in Std." SvgIcon={SsidChartIcon}>
            <Stack direction="column" justifyContent="center" alignItems="center" width="100%">
              <BarChart
                dataset={dataset}
                xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
                yAxis={[
                  {
                    colorMap: {
                      type: 'piecewise',
                      thresholds: [0],
                      colors: ['#ffa680', '#49a7cc'],
                    },
                  },
                ]}
                series={[{ dataKey: 'availableTime' }]}
                width={300}
                height={250}
                borderRadius={18}
                margin={{ top: 10, right: 10, bottom: 20, left: 30 }}
              />
            </Stack>
          </HomeCard>
        )}

        {evaluateTimes && (
          <HomeCard title="Arbeitszeit-Saldo" subtitle="je Monat in Std." SvgIcon={BarChartIcon}>
            <Stack direction="column" justifyContent="center" alignItems="center" width="100%">
              <LineChart
                dataset={dataset}
                xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
                yAxis={[{ min: 0, max: 100 }]}
                series={[{ dataKey: 'workingTime' }]}
                width={300}
                height={250}
                margin={{ top: 10, right: 10, bottom: 20, left: 30 }}
              />
            </Stack>
          </HomeCard>
        )}
      </Stack>
    </Stack>
  );
};

export default UserHomePage;
